import {
  Box,
  Button,
  Text,
  HStack,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RiSparklingFill } from "react-icons/ri";
import { config } from "../../assets/config";
import { useNavigate } from "react-router-dom";
import { MessageType, setMessage } from "../../store/messageSlice";
import { useAppDispatch } from "../../store/hooks";
import { setUnauthorized } from "../../store/authSlice";
import { getIdToken } from "../../utils/authTokenStorage";
import { ensureValidIdToken, refreshTokenWithLock } from "../../utils/awsCognito";

/**
 * パラメータ
 */
interface AIAnalysisModalProps {
  isAIModalOpen: boolean;
  setIsAIModalOpen: (isOpen: boolean) => void;
  title: string; // モーダルタイトル
  initialPrompt: string; // 初期プロンプト
  projectId?: string; // プロジェクトID（イベントログ記録用）
}

/**
 * HTMLエスケープ
 */
const escapeHtml = (str: string): string =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * 別ウィンドウの型定義
 */
type ResultWindow = Window & {
  appendText: (text: string) => void;
  updateStatus: (status: string, message: string) => void;
  renderMarkdown: () => void;
};

/**
 * 別ウィンドウ用のHTML生成
 */
const generateResultWindowHTML = (title: string, runningMessage: string): string => {
  const safeTitle = escapeHtml(title);
  const safeRunningMessage = escapeHtml(runningMessage);
  return `
<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeTitle}</title>
  <script src="/libs/marked.min.js"></script>
  <script src="/libs/purify.min.js"></script>
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      margin: 0;
      padding: 20px;
      background-color: #f8f9fa;
      color: #333;
      line-height: 1.6;
    }
    .header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid #e9ecef;
    }
    .header h1 {
      margin: 0;
      font-size: 1.5rem;
      color: #6b46c1;
    }
    .status {
      padding: 10px 15px;
      background-color: #e9ecef;
      border-radius: 8px;
      margin-bottom: 20px;
      font-size: 0.9rem;
    }
    .status.loading {
      background-color: #fff3cd;
      color: #856404;
    }
    .status.complete {
      background-color: #d4edda;
      color: #155724;
    }
    .status.error {
      background-color: #f8d7da;
      color: #721c24;
    }
    .content {
      background-color: white;
      padding: 25px;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      min-height: 300px;
    }
    .content h1, .content h2, .content h3 {
      color: #2d3748;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
    }
    .content h1:first-child, .content h2:first-child, .content h3:first-child {
      margin-top: 0;
    }
    .content h2 {
      font-size: 1.4rem;
      border-bottom: 1px solid #e9ecef;
      padding-bottom: 0.3em;
    }
    .content h3 {
      font-size: 1.2rem;
    }
    .content p {
      margin: 0.8em 0;
    }
    .content ul, .content ol {
      margin: 0.8em 0;
      padding-left: 2em;
    }
    .content li {
      margin: 0.4em 0;
    }
    .content strong {
      color: #6b46c1;
    }
    .content code {
      background-color: #f1f3f4;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: "SFMono-Regular", Consolas, monospace;
      font-size: 0.9em;
    }
    .content pre {
      background-color: #2d3748;
      color: #e2e8f0;
      padding: 15px;
      border-radius: 8px;
      overflow-x: auto;
    }
    .content pre code {
      background-color: transparent;
      padding: 0;
      color: inherit;
    }
    .content blockquote {
      border-left: 4px solid #6b46c1;
      margin: 1em 0;
      padding-left: 1em;
      color: #666;
    }
    .content table {
      border-collapse: collapse;
      width: 100%;
      margin: 1em 0;
    }
    .content th, .content td {
      border: 1px solid #e9ecef;
      padding: 8px 12px;
      text-align: left;
    }
    .content th {
      background-color: #f8f9fa;
    }
    .raw-text {
      white-space: pre-wrap;
      font-family: "SFMono-Regular", Consolas, monospace;
      font-size: 0.9rem;
    }
    .cursor {
      display: inline-block;
      width: 8px;
      height: 1em;
      background-color: #6b46c1;
      animation: blink 1s infinite;
      vertical-align: text-bottom;
      margin-left: 2px;
    }
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0; }
    }
  </style>
</head>
<body>
  <div class="header">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#6b46c1">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
    <h1>${safeTitle}</h1>
  </div>
  <div id="status" class="status loading">${safeRunningMessage}</div>
  <div class="content">
    <div id="rawContent" class="raw-text"></div>
    <span id="cursor" class="cursor"></span>
    <div id="renderedContent" style="display: none;"></div>
  </div>
  <script>
    // Markdownレンダリング用の関数
    function renderMarkdown() {
      const rawContent = document.getElementById('rawContent');
      const renderedContent = document.getElementById('renderedContent');
      const cursor = document.getElementById('cursor');

      if (rawContent && renderedContent && typeof marked !== 'undefined') {
        const markdown = rawContent.textContent || '';
        // XSS防止: marked.parseの出力をDOMPurifyでサニタイズ
        // DOMPurifyが未ロードの場合はプレーンテキストとして安全に表示
        const parsed = marked.parse(markdown);
        if (typeof DOMPurify !== 'undefined') {
          renderedContent.innerHTML = DOMPurify.sanitize(parsed);
        } else {
          renderedContent.textContent = markdown;
        }
        rawContent.style.display = 'none';
        renderedContent.style.display = 'block';
        if (cursor) cursor.style.display = 'none';
      }
    }

    // ステータス更新用の関数
    function updateStatus(status, message) {
      const statusEl = document.getElementById('status');
      if (statusEl) {
        statusEl.className = 'status ' + status;
        statusEl.textContent = message;
      }
    }

    // テキスト追加用の関数
    function appendText(text) {
      const rawContent = document.getElementById('rawContent');
      if (rawContent) {
        rawContent.textContent += text;
        // 自動スクロール
        window.scrollTo(0, document.body.scrollHeight);
      }
    }
  </script>
</body>
</html>
  `;
};

/**
 * AI生産能力分析
 */
const AIAnalysisModal: React.FC<AIAnalysisModalProps> = ({
  isAIModalOpen,
  setIsAIModalOpen,
  title,
  initialPrompt,
  projectId,
}) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [prompt, setPrompt] = useState<string>(""); // プロンプト内容を保持
  const [analysisStatus, setAnalysisStatus] = useState<"idle" | "running" | "complete" | "error">("idle");

  const resultWindowRef = useRef<ResultWindow | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const lambdaUrl = config.aiLambdaUrl;

  /**
   * 初期処理
   */
  useEffect(() => {
    setPrompt(initialPrompt);
  }, [initialPrompt]);

  /**
   * Markdown構文を修正する関数
   */
  const fixMarkdown = (text: string): string => {
    return text
      // 見出し記号の後にスペースを追加（ない場合）
      .replace(/^(#{1,6})([^\s#])/gm, '$1 $2')
      // 番号リストの後にスペースを追加（ない場合）
      .replace(/^(\d+\.)([^\s])/gm, '$1 $2')
      // **の前後にスペースがない場合の修正は難しいため、別途対応
      // **text** の形式が分断されていないかチェック
      .replace(/\*\*([^\*\n]+)\*\*/g, '**$1**'); // 既に正しい場合はそのまま
  };

  /**
   * AI分析開始処理
   */
  const handleAIAnalysis = async () => {
    setIsLoadingAI(true);
    setAnalysisStatus("running");

    // 別ウィンドウを開く
    // ポップアップブロッカーに阻止されないよう、クリックハンドラの同期実行の範囲内で
    // （await を挟む前に）window.open() を呼び出す必要がある
    const resultWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes') as ResultWindow | null;
    if (!resultWindow) {
      alert(t('components.AIAnalysisModal.alert.popupBlocked'));
      setIsLoadingAI(false);
      setAnalysisStatus("error");
      return;
    }

    resultWindowRef.current = resultWindow;
    resultWindow.document.write(generateResultWindowHTML(title, t('components.AIAnalysisModal.status.windowRunning')));
    resultWindow.document.close();

    // 認証チェック
    // 呼び出し前にidTokenの有効期限を確認し、期限切れ/間近であれば能動的にリフレッシュする
    // （レスポンスストリーミング対応のLambdaでは期限切れが401として返らないケースがあるため、
    //  後続の401チェックのみに頼らず、事前チェックを主とする）
    let idToken: string;
    try {
      idToken = await ensureValidIdToken();
    } catch (error) {
      console.error("Failed to obtain a valid idToken:", error);
      if (!resultWindow.closed) {
        resultWindow.updateStatus('error', t('components.AIAnalysisModal.status.windowError'));
      }
      dispatch(setMessage({
        message: t('common.api.authError'),
        type: 'error' as MessageType
      }));
      dispatch(setUnauthorized());
      navigate('/signin');
      return;
    }

    let fullText = "";

    // AbortController でリクエスト中断を可能にする
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // AIへのリクエスト送信
    const callAiLambda = (token: string) =>
      fetch(lambdaUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: token,
        },
        body: JSON.stringify({ mode: "aiAnalysis", prompt: prompt, projectId: projectId }),
        signal: abortController.signal,
      });

    try {
      let response = await callAiLambda(idToken);

      // idTokenの有効期限切れ等で401が返った場合は、トークンを更新して1回だけ再試行する
      if (response.status === 401) {
        await refreshTokenWithLock();
        const refreshedIdToken = getIdToken();
        if (!refreshedIdToken) {
          if (!resultWindow.closed) {
            resultWindow.updateStatus('error', t('components.AIAnalysisModal.status.windowError'));
          }
          dispatch(setMessage({
            message: t('common.api.authError'),
            type: 'error' as MessageType
          }));
          dispatch(setUnauthorized());
          navigate('/signin');
          return;
        }
        idToken = refreshedIdToken;
        response = await callAiLambda(idToken);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();

      if (reader) {
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // ウィンドウが閉じられたかチェック
          if (resultWindow.closed) {
            console.log("[AI-DEBUG] Result window was closed by user.");
            break;
          }

          // デコードしてチャンクを取得
          const chunk = decoder.decode(value, { stream: true });

          // バッファに追加して改行で分割
          buffer += chunk;
          const lines = buffer.split(/\r?\n/);

          // 未処理の部分を再びバッファに保管
          buffer = lines.pop() || "";

          // 各行を処理
          lines.forEach((line) => {
            if (line.startsWith("data:")) {
              const dataContent = line.slice(6); // "data: " の6文字を削除

              if (dataContent.trim() === "[DONE]") {
                console.log("[AI-DEBUG] Stream processing completed.");
                return;
              }

              if (dataContent.trim() === "") {
                // 空行の場合は改行を追加（段落区切り）
                fullText += "\n";
                if (!resultWindow.closed) {
                  resultWindow.appendText("\n");
                }
              } else {
                // 空行でない場合はそのまま連結
                fullText += dataContent;
                if (!resultWindow.closed) {
                  resultWindow.appendText(dataContent);
                }
              }
            }
          });
        }
      }

      // ストリーミング完了後、Markdownをレンダリング
      if (!resultWindow.closed) {
        // Markdown構文を修正
        const fixedText = fixMarkdown(fullText);

        // rawContentを修正後のテキストで更新
        const rawContentEl = resultWindow.document.getElementById('rawContent');
        if (rawContentEl) {
          rawContentEl.textContent = fixedText;
        }

        // ステータス更新とMarkdownレンダリング
        resultWindow.updateStatus('complete', t('components.AIAnalysisModal.status.windowComplete'));
        resultWindow.renderMarkdown();
      }

      setAnalysisStatus("complete");

    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log("[AI-DEBUG] Fetch request was aborted.");
      } else {
        console.error("Error while performing AI analysis:", error);
        if (!resultWindow.closed) {
          resultWindow.updateStatus('error', t('components.AIAnalysisModal.status.windowError'));
        }
        setAnalysisStatus("error");
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoadingAI(false);
    }
  };

  /**
   * モーダルを閉じる処理
   */
  const handleClose = () => {
    // 進行中のリクエストを中断
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoadingAI(false);
    setIsAIModalOpen(false);
    setAnalysisStatus("idle");
  };

  /**
   * ステータスメッセージ取得
   */
  const getStatusMessage = () => {
    switch (analysisStatus) {
      case "running":
        return t('components.AIAnalysisModal.status.running');
      case "complete":
        return t('components.AIAnalysisModal.status.complete');
      case "error":
        return t('components.AIAnalysisModal.status.error');
      default:
        return t('components.AIAnalysisModal.status.idle');
    }
  };

  /**
   * ステータスカラー取得
   */
  const getStatusColor = () => {
    switch (analysisStatus) {
      case "running":
        return "orange.500";
      case "complete":
        return "green.500";
      case "error":
        return "red.500";
      default:
        return "gray.500";
    }
  };

  return (
    <Modal
      isOpen={isAIModalOpen}
      onClose={handleClose}
      scrollBehavior="inside"
      size="xl"
      closeOnOverlayClick={false}
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxW="900px" containerProps={{ zIndex: 1500 }}>
        <ModalHeader bg="purple.50" borderTopRadius="md">
          <HStack justify="center" spacing={3}>
            <Box color="purple.600" fontSize="xl">
              <RiSparklingFill />
            </Box>
            <Text fontWeight="bold" fontSize="lg" color="purple.700">
              {title}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={6}>
          <Text fontWeight="bold" mb={3} color="gray.700">
            {t('components.AIAnalysisModal.label.promptContent')}
          </Text>
          <Text fontSize="sm" color="gray.500" mb={3}>
            {t('components.AIAnalysisModal.label.promptHelp')}
          </Text>

          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={50000}
            height="350px"
            resize="vertical"
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            fontFamily="monospace"
            fontSize="sm"
            lineHeight="1.6"
            p={4}
            _focus={{
              borderColor: "purple.400",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
            }}
          />

          <Box
            mt={4}
            p={3}
            bg={analysisStatus === "complete" ? "green.50" : analysisStatus === "error" ? "red.50" : analysisStatus === "running" ? "orange.50" : "gray.50"}
            borderRadius="md"
            border="1px solid"
            borderColor={analysisStatus === "complete" ? "green.200" : analysisStatus === "error" ? "red.200" : analysisStatus === "running" ? "orange.200" : "gray.200"}
          >
            <Text color={getStatusColor()} fontWeight="medium" fontSize="sm">
              {getStatusMessage()}
            </Text>
          </Box>
        </ModalBody>

        <ModalFooter bg="gray.50" borderBottomRadius="md">
          <Button
            onClick={handleAIAnalysis}
            isLoading={isLoadingAI}
            loadingText={t('components.AIAnalysisModal.button.analyzing')}
            variant="solid"
            colorScheme="purple"
            size="lg"
            leftIcon={<RiSparklingFill />}
            mr={3}
          >
            {t('components.AIAnalysisModal.button.startAnalysis')}
          </Button>

          <Button
            onClick={handleClose}
            variant="outline"
            colorScheme="gray"
            size="lg"
          >
            {t('components.AIAnalysisModal.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AIAnalysisModal;
